import React from 'react';
import { GridItem, VisuallyHidden } from '../../../../ui';
import FacebookIcon from '../../../../ui/new-assets/facebook.svg';
import InstagramIcon from '../../../../ui/new-assets/instagram.svg';
import TwitterIcon from '../../../../ui/new-assets/x-logo.svg';
import YouTubeIcon from '../../../../ui/new-assets/youtube.svg';
import { FooterSectionHeader } from './FooterSectionHeader';
import { ITranslationFunction } from '../../i18n/translate';
import { useTranslation } from '../../i18n/translate/useTranslation';
import styled from '@emotion/styled';

export enum ISocialLinkItemPlatform {
	Facebook = 'Facebook',
	Instagram = 'Instagram',
	Twitter = 'Twitter',
	YouTube = 'YouTube',
}

export interface ISocialLinkItem {
	url: string;
	platform: ISocialLinkItemPlatform;
}

export interface IFooterSocialLinksProps {
	title: string;
	links: ISocialLinkItem[];
}

export const FooterSocialLinks = ({
	title,
	links,
}: IFooterSocialLinksProps) => {
	const { t } = useTranslation();

	return (
		<GridItem columns={[3, { tablet: 6, mobileXL: 12 }]}>
			<FooterSectionHeader styling="h3">{title}</FooterSectionHeader>
			<ul className="flex flex-row justify-between mobile:justify-start mobile-xl:mb-10 gap-6 items-center pl-0 ml-0">
				{links.map((link) => renderLink(link, t))}
			</ul>
		</GridItem>
	);
};

function renderLink(
	{ platform, url }: ISocialLinkItem,
	t: ITranslationFunction,
) {
	const icon = getIconForPlatform(platform);

	return (
		<ListItem key={url}>
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className="text-white hover:opacity-[0.64] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 rounded-[2px] focus-visible:outline-highlight"
			>
				<VisuallyHidden as="span">
					{platform === ISocialLinkItemPlatform.YouTube
						? t('sr_only_footer_social_links_subscribe')
						: t('sr_only_footer_social_links_follow', { platform })}
				</VisuallyHidden>

				{icon}
			</a>
		</ListItem>
	);
}

function getIconForPlatform(platform: ISocialLinkItemPlatform) {
	switch (platform) {
		case 'Facebook':
			return (
				<FacebookIcon aria-hidden="true" className="size-9 tablet:size-6" />
			);

		case 'Instagram':
			return (
				<InstagramIcon aria-hidden="true" className="size-9 tablet:size-6" />
			);

		case 'Twitter':
			return (
				<TwitterIcon aria-hidden="true" className="size-9 tablet:size-6" />
			);

		case 'YouTube':
			return (
				<YouTubeIcon aria-hidden="true" className="size-9 tablet:size-6" />
			);

		default:
			throw new Error(`No icon available for platform: ${platform}`);
	}
}

const ListItem = styled.li`
	padding-left: 0;
`;
